import { useState, useRef, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";

const TagInput = ({ tags = [], onChange, placeholder = "Add tags..." }) => {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = (query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get("/api/tags/autocomplete/", {
          params: { q: query },
        });
        const existing = new Set(tags.map((t) => t.toLowerCase()));
        setSuggestions(
          res.data.filter((t) => !existing.has(t.name.toLowerCase()))
        );
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  };

  const addTag = (name) => {
    const cleaned = name.trim().toLowerCase();
    if (!cleaned || tags.some((t) => t.toLowerCase() === cleaned)) return;
    onChange([...tags, cleaned]);
    setInput("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const removeTag = (index) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (input.trim()) addTag(input);
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex flex-wrap gap-1.5 w-full border border-gray-200 rounded-xl px-3 py-2 bg-gray-50 focus-within:border-blue-400 focus-within:bg-white transition dark:bg-gray-700 dark:border-gray-600 dark:focus-within:bg-gray-700">
        {tags.map((tag, idx) => (
          <span
            key={idx}
            className="inline-flex items-center bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium px-2.5 py-1 rounded-full"
          >
            #{tag}
            <button
              type="button"
              onClick={() => removeTag(idx)}
              className="ml-1.5 text-blue-400 dark:text-blue-500 hover:text-blue-600 dark:hover:text-blue-300"
            >
              &times;
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            fetchSuggestions(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (input.trim()) addTag(input); }}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[80px] outline-none text-sm bg-transparent dark:text-white dark:placeholder-gray-400"
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => addTag(s.name)}
              className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              #{s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagInput;
